// viewFromPassport — Track B step 1. Derive each section's `view` (the prop the
// self-managed section renderers consume) from a LIVE passport (buildPassport output:
// input + system1 + system2), instead of the hand-authored boards in section*Boards.js.
//
// WHY THIS EXISTS. The passport-render (§I–§VII) is byte-parity-closed against the Claude
// Design canon, but every section is fed a hand-transcribed fixture today. To assemble a
// real document from any config/biography we need the boards derived from the engine. This
// module is that derivation; the assembly (step 2) and PDF (step 3) build on top.
//
// THREE FIELD CLASSES (discovered during the port — see the header of each deriver):
//   1. DETERMINISTIC — pure functions of (input + system1 + system2). These are what the
//      derivation OWNS, and what the svg-parity golden validates: for a board whose `source`
//      is a real dataset, viewFromPassport(datasetPassport) reproduces the board's
//      deterministic fields exactly (viewFromPassport.test.js is the oracle).
//   2. BROWSER-FIT — need DOM measurement (Newsreader getBBox / measured pagination):
//      §II `plan`, §III/§IV `labelMode`, §V `fit`, §VI `sheets`, §VII `fitClasses/dataFit`.
//      No closed form; the boards BAKE a one-time real-browser capture. The deriver carries
//      a baked estimate (the same value the board uses) and the client re-fit stays cosmetic
//      — the "labelMode contract" the sections already rely on for SSR.
//   3. ILLUSTRATIVE / WIZARD-AUTHORED — hand-chosen decoration or wizard content absent from
//      the engine datasets: §VII `pastLabels/horizon/nowYear` (CD picked round axis ticks that
//      do NOT match the dataset's stage years), §IV `bg1/bg2`, §II ratings. The deriver
//      computes the FAITHFUL value from input.stages when present (real biographies carry
//      them); it intentionally DIVERGES from CD's illustrative board values, so those fields
//      are excluded from the byte-parity oracle and covered by their own unit assertions.

import { silhouetteOf } from './silhouette.js';
import { filled } from '../insights/input.js';
import { READING_YEAR } from '../contracts.js';
import { deriveTickLabels } from './tickLabels.js';

// CD stores chart data as 3-decimal literals (means: [1.143, …], shapeII: 2). The chart maps
// value → y with toFixed(2), and 8/7 vs 1.143 round to different pixels — so a faithful
// derivation must adopt CD's 3-decimal convention to stay byte-identical to the golden.
const r3 = (x) => (Number.isFinite(x) ? Math.round(x * 1000) / 1000 : x);
const yy = (y) => "'" + String(Math.floor(y)).slice(-2); // 2004.7 → '04

// ─────────────────────────────────────────────────────────────────────────────
// §VII · CLOSING
// DETERMINISTIC: bucket (axis-D), means (silhouette), shapeI/shapeII (forward projection).
// ILLUSTRATIVE: pastLabels / nowYear / horizon — faithful from input.stages, null-degrade
//   when dates are absent (dataset 02/05/07, board v07 path). skipVi is cross-section (set by
//   the assembler once §VI presence is known); standalone it defaults to false.
// BROWSER-FIT: fitClasses/dataFit — baked (never a concession on the canon set).
// ─────────────────────────────────────────────────────────────────────────────
export function viewVII(passport, opts = {}) {
  const { screen, source, skipVi = false, readingYear = READING_YEAR } = opts;
  const means = passport.silhouette.map(r3);
  const N = means.length;
  // Пустой этап силуэта = null (s836.d3). personalBase/NOW читаем через filled() — форму, не
  // 0: ноль занижал базу и флипал bucket в ложный `positive` (замер 3de19875…08bf, секв. A).
  // filled() остаётся ЗДЕСЬ: в `v.means` (и в паспорт) уходит сырой массив с null, не интерп-
  // числа (s4-handoff п.4 / как E2, s495.d1 Р3). На корпусе null нет → числа байт-идентичны.
  const f = filled(means);
  const past = f.slice(0, -1);
  const personalBase = past.length ? past.reduce((a, c) => a + c, 0) / past.length : f[N - 1];
  // axis-D: strictly-greater → positive; tie (NOW == personal base) → downward (s276.d5).
  const bucket = f[N - 1] > personalBase ? 'positive' : 'downward';

  const stages = passport.input?.wizard?.stages || null;
  const hasDates = !!stages && stages.length === N && stages.every((s) => Number.isFinite(s.startY));
  // ПОРЯДОК ВАЖЕН, и он был неверным до QA-прогона cc-31: условием стоял `current`, поэтому
  // реальный, проставленный `endY` текущего этапа ВЫБРАСЫВАЛСЯ в пользу года чтения. Сабмит,
  // сделанный в 2027, печатал 2026 — и печатал во всех трёх секциях сразу, так что второго
  // мнения у читателя не было. Канон-датасеты этого не ловили: у них `endY = 2026.6`, и обе
  // ветки дают один и тот же 2026. Правило: реальный конец побеждает; якорь — только когда
  // конца нет и этап открыт (`nowY` не передали); иначе начало.
  // §VII отличается от §I/§II намеренно, и это не расхождение правила. Там `arcYears`/`years` —
  // УТВЕРЖДЕНИЕ о размахе жизни, и при пустом конце оно снимается. Здесь `nowYear` — точка на
  // оси прямой проекции, ось обязана где-то кончаться. Поэтому при отсутствующем конце — год
  // чтения, а не начало последнего этапа (то дало бы ось, кончающуюся там, где глава началась).
  // Три ветки, а не две (QA cc-31). Раньше при пустом конце сюда безусловно вставал год чтения —
  // и §VII строил проекцию от якоря на паспорте, где §I и §II УЖЕ отказались печатать хоть что-то:
  // четыре секции, три разных рассказа про одно «сейчас». Плюс на этапах позже года чтения
  // горизонт выходил отрицательным, и лист печатал «another -1 years of this?».
  // ЯКОРЬ ПРОЕКЦИИ ЗАЛОЧЕН НА ГОД ЧТЕНИЯ, когда последний этап ТЕКУЩИЙ (правило CD, ревью 3).
  //
  // Его конец в этом случае — не утверждение человека, а отпечаток момента сабмита, и мерить по
  // нему пролёт жизни значит принять оценку за замер. У ЗАКРЫТОГО последнего этапа конец —
  // настоящее утверждение, и якорь берётся из него.
  //
  // Я это уже нарушал дважды и по-разному: сначала померил пролёт до 2026.6 (число выросло на
  // единицу молча), а потом — в замере краевых состояний — тихо ввёл третье правило «якорь
  // двигается за данными», чтобы обойти отрицательный пролёт на этапах из будущего. CD поймал:
  // если якорь двигается там, он двигается и при 2026.6, ради чего возврат и делался.
  // ЯКОРЬ ЗАЛОЧЕН ЦЕЛИКОМ — от данных не зависит вовсе (CD, ревью 4).
  //
  // Предыдущая версия ставила гейт на флаг `current`: у закрытого последнего этапа конец брался
  // из данных. CD назвал это ЧЕТВЁРТЫМ правилом, введённым на ходу, и был прав — я не заметил.
  // Замер: один датасет, флаг снят и поднят. На каноне число не шевелилось (`floor(2026.6)`
  // случайно совпал с годом чтения — корпус опять выродился и спрятал разницу), а на сабмите
  // 2027 шевелилось: 22 при поднятом флаге и 23 при снятом. **Один и тот же документ печатал
  // разное число в зависимости от флага, которого на листе нет.**
  //
  // Запись жизни, кончающаяся раньше года чтения, — это «stale-жизнь» из решения 05.08:
  // легальное состояние, где проекция строится от чтения, а не от последней записи.
  const anchorYear = hasDates ? readingYear : null;
  // ПРОЛЁТ МЕРИТСЯ ДО ЯКОРЯ, В ЦЕЛЫХ ГОДАХ (правило Claude Design, ревью 2 · 21.08).
  //
  // Я померил пролёт до `endY` последнего этапа — до 2026.6 — и число на закрывающей странице
  // выросло на единицу («another 18 years» → «another 19») молча, на всех четырёх датасетах.
  // CD вернул, и довод сильнее арифметики: **2026.6 — не утверждение человека.** Он сказал
  // «этап текущий»; точка-шесть это отпечаток момента сабмита. Печатать её как измеренный пролёт
  // жизни значит принять оценку за замер — в чистом виде число, которое после нас никто не
  // проверяет.
  //
  // Плюс геометрия: `NOW_X` на оси §VII стоит на ЯКОРЕ, а метки будущего считаются `nowYear + o`.
  // Пролёт до 2026.6 означал бы, что напечатанное число описывает один отрезок, а нарисованная
  // ось — другой. Тот же класс «разное разрешение», только впечатанный в лист.
  //
  // Канон подтверждает арифметикой: v01 — `nowYear 2026`, первый этап 2004.7, `horizon 22`, и
  // 2026 − 2004 = 22 ровно; v06 — 2026 − 2006 = 20. Обе стороны оси в одних единицах.
  // Якорь не позже начала жизни ⇒ пролёта нет: горизонт снимается, аннотация не печатается
  // (форма v07, `duration_null`). Ответ CD на краевой случай «все этапы позже якоря»: рисовать
  // ось по данным законно, но проекция строится от якоря, и отрицательного зеркала не бывает.
  const rawSpan = anchorYear === null ? null : anchorYear - Math.floor(stages[0].startY);

  const v = {
    ...(screen != null ? { screen } : {}),
    ...(source != null ? { source } : {}),
    bucket,
    means,
    // F4 (канон §VII, s974.d2): годы начала КАЖДОГО этапа — подписи левых границ N колонок,
    // включая последний; было N − 1 (точки на границах, последняя = now).
    pastLabels: hasDates ? stages.map((s) => yy(s.startY)) : null,
    nowYear: anchorYear === null ? readingYear : anchorYear,
    // future mirror of the past span, capped at 25y (s416); NO floor (s416.d1); null → static labels.
    // Отрицательный пролёт — не горизонт: «another -1 years of this?» на бумаге хуже отсутствия.
    // F4: пролёт 0 (все этапы начались в год чтения) — законный горизонт 0, рендер читает его как
    // H = 1 («another year of this?», канон §VII F4 борд 11). Отрицательный по-прежнему null.
    horizon: rawSpan !== null && rawSpan >= 0 ? Math.min(25, rawSpan) : null,
    // ПРОЛЁТ ДО КАПА — служебное поле, на лист не идёт (CD, ревью 4).
    //
    // Пока его не было, единственная проверка горизонта писалась как `Math.min(25, якорь − тик)`,
    // и на v03/v04 это работало амнистией: при пролёте больше 25 сходились и 28, и 30, и 47 —
    // ровно там, где число самое большое, проверки не было вовсе. Кап нельзя сверять тем же
    // выражением, которым он посчитан: нужны ДВА числа — что намерили и что напечатали.
    horizonRaw: rawSpan,
    skipVi: !!skipVi,
  };
  if (bucket === 'downward') {
    const fwd = passport.system1.redLine.forward || {};
    v.shapeI = r3(fwd.shapeI);
    v.shapeII = r3(fwd.shapeII);
  }
  // data-degradation marker (canon F2 · s417). skip_vi is cross-section (deterministic); the
  // dates/ceiling markers depend on the wizard-authored years, so they're honest from input but,
  // like pastLabels/horizon, diverge from CD's illustrative boards → not oracle-asserted.
  //
  // МАРКЕР СОБИРАЕТСЯ ИЗ НЕЗАВИСИМЫХ ФАКТОВ, А НЕ ЛЕСТНИЦЕЙ (починка cc-32).
  //
  // Лестница врала на входе «все этапы позже якоря»: дат нет — маркер `stage_dates_null ·
  // duration_null`, и это верно (форма v07); но при ЖИВЫХ датах из будущего пролёт тоже
  // непозитивный, и та же ветка печатала `stage_dates_null` рядом с напечатанным тиком `'30`.
  // Лист одновременно утверждал «дат нет» и показывал дату. Замер 21.08, состояние борда 9.
  //
  // Прошлые проверки этого не видели, потому что просили `toContain('duration_null')` — такая
  // форма проходит при любой лжи слева от точки. Факта два, и они независимы:
  //   • подписи прошлого сняты  → `stage_dates_null`  (pastLabels === null)
  //   • горизонта нет           → `duration_null`     (horizon === null)
  // На пустых датах верны оба — маркер v07 воспроизводится байт в байт. На будущем — только
  // второй. Порядок фиксирован, чтобы строка не зависела от порядка проверок.
  const marks = [
    ...(skipVi ? ['skip_vi'] : []),
    ...(v.pastLabels === null ? ['stage_dates_null'] : []),
    ...(v.horizon === null ? ['duration_null'] : []),
    ...(rawSpan !== null && rawSpan > 25 ? ['horizon_ceiling'] : []), // кап реально сработал
  ];
  if (marks.length) v.degr = marks.join(' · ');
  return v;
}

// ─────────────────────────────────────────────────────────────────────────────
// §IV · THE RED LINE (carrier)
// DETERMINISTIC (svg-parity oracle, deep-equal): carrierConfig / geometryClass / levelBand /
//   members / carrierSourceLines / carrier / carrierIdx / standing / evidence — all pure from
//   system1 + system1.currentStanding + system2.
//   · carrier rounds to 4 decimals (CD's §IV data convention; §VII used 3).
//   · carrierSourceLines applies the ≥7-member omit ("all seven" duplicates §III → []).
//   · split-family display members = [dominant carrier, the opposing line] (render order,
//     matches the "X vs Y" eyebrow); eyebrowMembers mirrors it.
//   · carrierIdx = passport.carrierStages.indices (System-1 derived, same list §II/§V read,
//     s454 · D&R s486.d1) — UNCAPPED; the EV_CAP display cap lives at the cards (SectionIV).
// INPUT (present only when the biography/dataset carries stages): stages (MM/YYYY + names).
//   §IV `bg1/bg2` are wizard step-3 deck choices, WIRED from input.wizard.buttons like §II —
//   ABSENT from the engine datasets, so they are excluded from the byte-parity oracle. The chart
//   X-axis needs MM/YYYY, converted from decimal years. So full render-parity is only reachable
//   with full wizard input, not from the engine fixtures.
// BROWSER-FIT: labelMode (fitStageLabels) — baked 'full' default; client re-fit is cosmetic.
// ─────────────────────────────────────────────────────────────────────────────
// ── РАЗМАХ ЗАПИСАННОЙ ЖИЗНИ — ОДНО МЕСТО НА ВЕСЬ ДОКУМЕНТ ────────────────────────────────────
//
// «До какого года дошла эта жизнь» печатают три секции: §I дугой на обложке, §II строкой годов,
// §III числом «N years on one map». Правило жило ТРЕМЯ рукописными копиями прямо здесь, в шве,
// и каждая несла комментарий «тот же порядок, что в §I» — то есть авторы копий знали, что копируют.
//
// Копии сошлись на КОНЦАХ и разошлись на АРИФМЕТИКЕ. §I и §II печатали `floor(начало) → floor(конец)`,
// §III печатала `round(конец − начало)`. На целых годах это тождество, на дробных — расхождение в
// единицу: лист печатал «2004 → 2026» и через страницу «23 years on one map».
//
// Почему это не поймали ни 1481 тест, ни парити, ни оракул: визард отдаёт МЕСЯЧНОЕ разрешение, а
// весь корпус был целогодовым либо канонным, а у канона начало .7 и конец .6 — разрыв дробных
// частей −0.1, при котором floor и round совпадают. Класс входов, различающий два правила, в
// корпусе отсутствовал. Замер 21.08: расходятся 5 входов из 11 на первом же непустом корпусе.
//
// Правило одно: **число лет = размах тех годов, которые документ НАПЕЧАТАЛ.** Не длительность
// жизни в календарном смысле — она у человека дробная, а на листе дробных годов не бывает нигде.
// Если обложка сказала «2004 → 2026», то «22 years», и никакая третья арифметика этого не меняет.
//
// ⚠️ Новую секцию, печатающую размах, ПОДКЛЮЧАТЬ СЮДА, а не переписывать четвёртой копией.
function lifeArc(passport, readingYear = READING_YEAR) {
  // Меты этапов годны, только когда они ложатся 1:1 на силуэт: дырявый или разъехавшийся список
  // рассинхронизирует два параллельных массива. Гард был только у §I — то есть на битом паспорте
  // обложка молчала, а §II и §III продолжали печатать годы. Теперь деградируют вместе (cc-32).
  //
  // Лазейка «нет `stageCount` → верим списку» здесь БЫЛА, а у §I (`:569`) её не было — и на
  // паспорте без `stageCount` обложка печатала датированную дугу «2000 → 2010» над чартом без
  // единого тика (`tickLabels: null`, `curIdx: NaN`). То есть гард, поставленный ради «деградируют
  // вместе», сам их и расщеплял. Снята QA cc-34; замер снятия — ноль красных, ни канон, ни
  // датасеты на ней не стояли. Возврат лазейки теперь ловит `meta-alignment.test.jsx`.
  const raw = passport.input && passport.input.wizard && passport.input.wizard.stages;
  const n = passport.stageCount;
  const st = Array.isArray(raw) && raw.length === n
    && raw.every((s) => s && typeof s === 'object') ? raw : [];
  const firstS = st[0] || null;
  const lastS = st.length ? st[st.length - 1] : null;
  // Открытое начало → крайнего года нет вовсе, и молчат все три (канон F7 v06: «5 stages» без годов).
  const openStart = !!(firstS && firstS.openStart);
  const first = openStart ? null : (firstS ? firstS.startY : null);
  // Порядок веток: реальный конец → якорь у ОТКРЫТОГО текущего этапа → конца нет, дуга не печатается.
  // Якорь-гард: год чтения не закрывает дугу, если он раньше начала этапа, который им закрывают, —
  // иначе «2000 → 2026» над строкой «01/2027 — present», а на краю прямая инверсия «2027 → 2026».
  const anchor = lastS && lastS.current && Number.isFinite(lastS.startY) && readingYear >= lastS.startY
    ? readingYear : null;
  const last = lastS
    ? (Number.isFinite(lastS.endY) ? lastS.endY : (lastS.current ? anchor : null))
    : null;
  // Number.isFinite, не typeof: NaN проходит проверку типа и печатается на бумаге («2000 → NaN»).
  const dated = Number.isFinite(first) && Number.isFinite(last);
  const firstY = dated ? Math.floor(first) : null;
  const lastY = dated ? Math.floor(last) : null;
  return {
    first, last, dated, firstY, lastY,
    // Печатаемые годы могут стоять в обратном порядке только на битом вводе; размаха тогда нет.
    years: dated && lastY >= firstY ? lastY - firstY : null,
  };
}

const LK = ['Vitality', 'Security', 'Growth', 'Authorship', 'Wanting', 'Yourself', 'Lasting'];
const r4 = (x) => (Number.isFinite(x) ? Math.round(x * 10000) / 10000 : x);
const slidersOf = (passport) => passport.input.wizard.sliders.map((s) => (s && s.values ? s.values : s));
// decimal year → 'MM/YYYY' (inverse of §IV pdate: y + (m-0.5)/12); clamps month to 1..12.
const mmYYYY = (y) => {
  if (!Number.isFinite(y)) return null;
  const yr = Math.floor(y);
  const m = Math.min(12, Math.max(1, Math.round((y - yr) * 12 + 0.5)));
  return String(m).padStart(2, '0') + '/' + yr;
};
// wizard step-3 deck choices (BG1–BG4). Shared by §II and §IV. BG1 stays an ARRAY (the render
// maps it with per-item spans + the ' · ' separator); BG2–BG4 print as ONE joined string. An
// empty/absent group → null so the render omits the slot ([] would render an empty element.)
const BG_SEP = ' · '; // mirrors SectionII BG1_SEP — multi-select join (provisional, s369.d2)
const bgArr = (sel) => (Array.isArray(sel) && sel.length ? sel.slice() : null);
const bgJoin = (sel) => (Array.isArray(sel) && sel.length ? sel.join(BG_SEP) : null);

export function viewIV(passport, opts = {}) {
  const { screen, source, id = 'iv', labelMode = 'full' } = opts;
  const rl = passport.system1.redLine;
  const sl = slidersOf(passport);

  const split = rl.carrierConfig === 'split';
  const opposing = rl.carrierSourceLines[0]; // opposition → [opposing] (render_keys)
  const members = split ? [rl.members[0], opposing] : rl.members.slice();

  let carrierSourceLines = rl.carrierSourceLines.slice();
  if (carrierSourceLines.length >= 7) carrierSourceLines = []; // "all seven" = §III dup

  const s2 = passport.system2 && passport.system2.sectionIV;
  const evidence = s2 && Array.isArray(s2.evidence)
    ? s2.evidence.map((e) => ({ stage: e.stage, quote: e.quote }))
    : [];
  // ONE carrier-stage list — the System-1 derived field §II/§V already read
  // (passport.carrierStages.indices), NOT System 2 evidence stages. The two agree whenever a
  // judge ran, but System 2 is null on the whole offline path, and the evidence line must stay
  // drawn there — quotes go silent, the line does not (s454 · D&R s486.d1). §IV was the last
  // section still keying off evidence; this migrates it, so §II/§IV/§V and the passport all count
  // the carrier the same way. UNCAPPED: the EV_CAP display cap belongs to the render (the cards),
  // so the eyebrow counter and the evidence subhead read the TRUE M, not the capped six (s671.d1).
  const carrierIdx = (passport.carrierStages && Array.isArray(passport.carrierStages.indices))
    ? passport.carrierStages.indices.slice()
    : [];

  // pale-line values: only the carrierSourceLines keys are drawn (extra keys are inert).
  // Untouched = null, not 0 (Н42 · s950.d1): `Number(x) || 0` drew an answer nobody gave. The
  // render fills the gap with the same fillCarrier the red line uses — one rule for both lines.
  const touchedOf = passport.input.wizard.sliders.map((s) => (s && s.touched) || null);
  const layerValues = Object.fromEntries(
    carrierSourceLines.map((k) => [k, sl.map((s, i) => {
      const x = s?.[k];
      if (x == null || (touchedOf[i] && touchedOf[i][k] === false)) return null;
      return Number(x) || 0;
    })]),
  );

  // stages from input when present (biographies carry them; engine datasets often don't).
  // bg1/bg2 are WIRED from input.wizard.buttons (шаг 3 deck), parallel to §II (viewII) — before
  // this they were hardcoded null and the evidence pills + `theMove` label clause were dead on a
  // live document (D6 · s650.d1). §IV reads only BG1 (trigger pill + the-move labels) and BG2
  // (fuel pill); BG3/BG4 are §II-only. bg1 stays an ARRAY (SectionIV maps it), bg2 a joined
  // string. Wizard content → excluded from the §IV byte-parity oracle (illustrative board values).
  const inStages = passport.input.wizard.stages;
  const buttons = passport.input.wizard.buttons || [];
  const stages = Array.isArray(inStages)
    ? inStages.map((s, i) => {
        const b = buttons[i] || {};
        return {
          name: s.name, roman: undefined,
          start: mmYYYY(s.startY), end: s.endY == null ? null : mmYYYY(s.endY),
          current: !!s.current, openStart: !!s.openStart,
          bg1: bgArr(b.BG1), bg2: bgJoin(b.BG2),
        };
      })
    : null;

  const v = {
    id, ...(screen != null ? { screen } : {}), ...(source != null ? { source } : {}),
    carrierConfig: rl.carrierConfig,
    geometryClass: rl.geometryClass,
    levelBand: rl.levelBand,
    members,
    ...(split ? { eyebrowMembers: members.slice() } : {}),
    carrierSourceLines,
    carrier: rl.carrier.map(r4),
    carrierIdx,
    layerValues,
    standing: passport.system1.currentStanding,
    evidence,
    stages,
    labelMode,
  };
  return v;
}

// ─────────────────────────────────────────────────────────────────────────────
// §III · SEVEN LAYERS
// DETERMINISTIC: standing (= system1.currentStanding). INPUT: stages (+roman), sliders (flat
//   values), touched (only when a layer was left untouched), datesIncomplete. BROWSER-FIT:
//   labelMode (fitStageLabels) — baked; client re-fit cosmetic. generatedAt falls back to the
//   section's SNAPSHOT_STAMP when absent, so the deriver leaves it unset.
// §III renders fully from the engine datasets that carry stages (01/04/06/08) → render-parity.
// ─────────────────────────────────────────────────────────────────────────────
const ROMAN = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X', 'XI', 'XII', 'XIII', 'XIV', 'XV'];

export function viewIII(passport, opts = {}) {
  const { screen, source, id, labelMode = 'full', readingYear = READING_YEAR } = opts;
  const rawSliders = passport.input.wizard.sliders;
  const sliders = rawSliders.map((s) => (s && s.values ? { ...s.values } : { ...s }));
  const touchedInfo = {};
  let anyUntouched = false;
  rawSliders.forEach((s) => {
    if (s && s.touched) for (const k of LK) if (s.touched[k] === false) { touchedInfo[k] = false; anyUntouched = true; }
  });

  const inStages = passport.input.wizard.stages || [];
  const stages = inStages.map((s, i) => ({
    roman: ROMAN[i], name: s.name, startY: s.startY, endY: s.endY,
    current: !!s.current, openStart: !!s.openStart,
  }));
  // ПРОЛЁТ СЧИТАЕТ ШОВ (cc-31, QA). Строка «N years on one map» — то же утверждение о размахе
  // жизни, что дуга §I и строка §II, но правило жило ТРЕТЬЕЙ копией в самом рендерере, и копия
  // разошлась: на паспорте с открытым началом §I печатала «5 stages» без годов, §II «2004 →
  // 2026», а §III — «17 years on one map», где 17 это пролёт от начала ВТОРОГО этапа. Три
  // секции, три ответа, один лист. Гард шва этого не видел: `viewIII` отдавал не ссылку на
  // объект визарда, а копию — проверялась идентичность ссылки, а не класс, который она несёт.
  // Копия правила снята (cc-32): считает `lifeArc`, тот же источник, что у дуги §I и строки §II.
  // Раньше здесь стоял `Math.round(конец − начало)` — третья арифметика того же утверждения.
  const years = lifeArc(passport, readingYear).years;
  const datesIncomplete = inStages.some((s) => !Number.isFinite(s.startY) || (!s.current && !Number.isFinite(s.endY)));

  return {
    ...(id != null ? { id } : {}), ...(screen != null ? { screen } : {}), ...(source != null ? { source } : {}),
    stages, sliders,
    years, // пролёт — считает шов, не рендерер (см. выше)
    standing: passport.system1.currentStanding,
    ...(anyUntouched ? { touched: touchedInfo } : {}),
    ...(datesIncomplete ? { datesIncomplete: true } : {}),
    labelMode,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// §VI · TRANSCRIPT (quotes)
// DETERMINISTIC: stack / qn = system2.sectionVI.quoteStack / quoteN, each stage's `field` and
//   `reason` from system2.perStage. Numbering/denom and the silent-skip / section_absent
//   decision are recomputed inside SectionVI after filtering reason cards — the deriver just
//   carries the honest data. INPUT: stage names/roman/current.
//   F5 (s882.d1): NO `transport` flag is emitted — a transport failure is not a printable state,
//   it collapses to the same section-removal path as N < 2 (system2.transport = telemetry only).
// BROWSER-FIT: sheets (measured pagination) — baked; single-sheet default when unknown.
// ─────────────────────────────────────────────────────────────────────────────
export function viewVI(passport, opts = {}) {
  const { screen, source, sheets } = opts;
  const s2 = passport.system2 && passport.system2.sectionVI;
  const per = (passport.system2 && passport.system2.perStage) || [];
  const at = (stage) => per.find((p) => p.stage === stage) || {};
  const fieldOf = (stage) => at(stage).field || null;
  const reasonOf = (stage) => at(stage).reason || null;

  const names = (passport.input.wizard.stages || []).map((s) => s.name);
  const stages = names.map((n, i) => ({ name: n, roman: ROMAN[i], current: i === names.length - 1 }));
  // The quote_n stage index is the LAST stage — keyed off the slider count (always present), not
  // the stage-NAME count. Otherwise a passport with system2 but no wizard.stages (e.g. 07-long)
  // drops a real quote_n and under-counts, which would wrongly remove §VI in documentPlan.
  const N = passport.stageCount ?? names.length;

  // A silent / empty card ALWAYS carries a truthy reason so it is filtered out of the count and
  // the layout (silent skip). Fall back to a sentinel when perStage has no aligned reason —
  // never emit reason:null on an empty card (that would count as a real quote and print blank).
  const toCard = (stage, text, silent) =>
    silent || !text
      ? { stage, field: null, text: '', reason: reasonOf(stage) || 'no_quote' }
      : { stage, field: fieldOf(stage), text, reason: null };

  const stack = s2 && Array.isArray(s2.quoteStack)
    ? s2.quoteStack.map((it) => toCard(it.stage, it.text, it.silent))
    : [];
  const qn = s2 && N > 0 ? toCard(N - 1, s2.quoteN, s2.quoteNSilent) : null;

  // F5 (s882.d1): the view no longer emits a `transport` flag. A judge transport failure is not
  // a printable §VI state — the engine marks every card with a reason (viewVI sees an all-reason
  // stack → realQuoteCount 0 < MIN_QUOTES), so the section is removed and §VII renumbers, same as
  // an empty run. `passport.system2.transport` stays as run-level telemetry (read directly by
  // analytics, s311.d6); it must not drive the rendered page.

  return {
    ...(screen != null ? { screen } : {}), ...(source != null ? { source } : {}),
    stages, stack, qn,
    ...(sheets ? { sheets } : {}),
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// §V · PATTERN
// DETERMINISTIC: family (mainPattern.family), carrierConfig (redLine.carrierConfig),
//   nameMembers (redLine.members; split → [dominant, opposing]; scatter → []), carrierStages
//   (= evidence stages, system2.sectionIV), N, layers (per-active-layer slider arrays), cards
//   (system1.cards flattened: detection fields + cell + stub, where stub folds in the route as
//   `${stub}.${route}` — CD's board convention). Catalog CELL/STUB copy lives in SectionV.jsx,
//   keyed by these pointers.
// ENGINE-flag: `absent` (§V removed) — the zero_spread_all_layers trigger is RATIFIED (s417.d1)
//   and now lives in System 1 (index.js sets meta.sectionAbsent + empties cards). The deriver
//   just reads it. `cascade` is set by the assembler (§V AND §VI both absent).
// BROWSER-FIT: fit (fitSheet) — baked; only v14 triggers on the canon set.
// ─────────────────────────────────────────────────────────────────────────────
function mapCard(c) {
  const det = c.detection || {};
  const out = { type: det.type, layers: det.layers };
  if (det.dir !== undefined) out.dir = det.dir;
  if (det.stage !== undefined) out.stage = det.stage;
  if (det.stages !== undefined) out.stages = det.stages;
  if (det.trough !== undefined) out.trough = det.trough;
  if (det.dominantBlock !== undefined) out.dominantBlock = det.dominantBlock;
  out.cell = c.cell ?? null;
  // cc-30: F.3 carries its sub-key as `route`, F.5 carries it as `variant` (cards.js:201,
  // `stub('F.5', { variant: f5Variant(L) })`). Only `route` was read here, so a live F.5 arrived
  // as the bare `'F.5'` while the STUB bank holds `F.5.active-rebuild` / `F.5.biological` — the
  // card could never resolve and rendered wordless. The copy exists; only the pointer was lost.
  // Board v13 carries `'F.5.active-rebuild'` typed by hand, which is why parity stayed green.
  const subKey = c.route ?? c.variant ?? null;
  out.stub = c.stub ? (subKey ? `${c.stub}.${subKey}` : c.stub) : null;
  return out;
}

export function viewV(passport, opts = {}) {
  const { screen, source, cascade, fit } = opts;
  const s1 = passport.system1;
  const sl = slidersOf(passport);
  const N = sl.length;
  const active = (s1.meta && s1.meta.activeLayers) || LK;
  const activeInOrder = LK.filter((k) => active.includes(k));

  const cfg = s1.redLine.carrierConfig;
  const split = cfg === 'split';
  const nameMembers = cfg === 'scatter'
    ? []
    : split
      ? [s1.redLine.members[0], s1.redLine.carrierSourceLines[0]]
      : s1.redLine.members.slice();

  // System-1 derived field (passportData.js). Read it directly instead of reconstructing from
  // System 2 §IV evidence: the two agree 8/8, but System 2 is null offline and the evidence
  // line must stay drawn (s454 · D&R s486.d1).
  const carrierStages = (passport.carrierStages && Array.isArray(passport.carrierStages.indices))
    ? passport.carrierStages.indices.slice()
    : [];

  const layers = Object.fromEntries(activeInOrder.map((k) => [k, sl.map((s) => Number(s?.[k]) || 0)]));

  // zero_spread_all_layers is decided in System 1 (index.js, ratified s417.d1): it sets
  // meta.sectionAbsent and empties cards. The deriver just reads the engine's verdict.
  const sectionAbsent = !!(s1.meta && s1.meta.sectionAbsent);
  const cards = (s1.cards || []).map(mapCard);

  const v = {
    ...(screen != null ? { screen } : {}), ...(source != null ? { source } : {}),
    N,
    family: s1.mainPattern.family,
    carrierConfig: cfg,
    nameMembers,
    carrierStages,
    layers,
    cards,
    ...(sectionAbsent ? { absent: true } : {}),
    ...(cascade ? { cascade: true } : {}),
    ...(fit ? { fit } : {}),
  };
  return v;
}

// ─────────────────────────────────────────────────────────────────────────────
// §II · TIMELINE
// DETERMINISTIC: means (silhouette, FULL precision — §II keeps the full-precision touched-mean
//   (÷touched, s446.d4), unlike §VII's 3-decimal chart data), carriers (= evidence stages, the
//   §IV carrier stage list), years
//   ("first → now" from input.stages). INPUT/WIZARD (absent from the engine geometry): stage
//   rating (wizard step-3 sine) and dur stay null; bg1..bg4 are now WIRED from
//   input.wizard.buttons (шаг 3 deck; бывш. s-id «step-6» BG1–BG4, contract commit 12bc08b) — see below. So §II
//   render-parity needs full wizard input; the engine oracle covers means + carriers + years.
// BROWSER-FIT: plan (measured per-sheet pagination) — baked.
//
// BG WIRING (s430 · G6-1-provisional = show ALL labels, NOT primary-only, per s369.d2):
//   each stage shows every selected label per group. BG1 stays an ARRAY — SectionII maps it
//   with per-item spans + the provisional ' · ' separator (BG1_SEP). BG2–BG4 the render prints
//   as ONE string, so we join the selections here with the same ' · ' (mirrors BG1_SEP). An
//   empty/absent group → null so the render omits the slot ([] would render an empty <dd>).
//   The current stage's BG4 is DERIVED faithfully but never shown: SectionII owns the
//   "— still in motion" rule for the what's-left slot (render is the single owner of that).
//   These fields are wizard content → excluded from the §II byte-parity oracle by design (the
//   illustrative board bg values in sectionIIBoards.js intentionally differ from the datasets).
// ─────────────────────────────────────────────────────────────────────────────
// BG_SEP / bgArr / bgJoin are defined once above §IV (shared by §II and §IV).

export function viewII(passport, opts = {}) {
  const { screen, source, plan, readingYear = READING_YEAR } = opts;
  const means = silhouetteOf(passport.input.wizard.sliders);
  // System-1 derived field, the SAME source §V reads (see viewV above, s454 · D&R s486.d1).
  // §II read System 2 §IV evidence until cc-30 and was the last section still doing so. The two
  // agree when a judge ran, but System 2 is null on the whole offline path — including
  // scripts/assemble-doc.mjs on a biography — so `carriers` came out empty on all 29 extracts
  // while carrierStages.indices was populated. §II then printed "no single stage carries the red
  // line on its own" with zero ✦ markers, one page before §IV named the carrier and §V listed
  // those very stages. Parity stayed green because the boards carry hand-set carriers in the
  // fixture; the live passport had never been through this path.
  const carriers = (passport.carrierStages && Array.isArray(passport.carrierStages.indices))
    ? passport.carrierStages.indices.slice()
    : [];
  const inStages = passport.input.wizard.stages || [];
  const buttons = passport.input.wizard.buttons || [];
  const stages = inStages.map((s, i) => {
    const b = buttons[i] || {};
    return {
      name: s.name,
      start: mmYYYY(s.startY),
      end: s.endY == null ? null : mmYYYY(s.endY),
      current: !!s.current,
      ...(s.openStart ? { openStart: true } : {}),
      // cc-30: the canon degradation proven by board v04 — drop the whole date row — was
      // unreachable from a live passport because nothing derived this flag. With no date on
      // EITHER side the card rendered `<span></span> — <span></span>`: a bare em-dash between
      // two empty spans. The row is meaningful if either side can print: the left is
      // "(open)" or the start, the right is "present" or the end.
      ...((s.openStart || Number.isFinite(s.startY)) || (s.current || Number.isFinite(s.endY))
        ? {} : { datesMissing: true }),
      // dur/rating сняты (cc-113, спина G-II): оба всегда были null и рендер их не берёт —
      // RATING-бар рисуется из `means` (s836.d2, SectionII rating_bar), `s.dur` фильтровался
      // `s.dur && …` в SectionII:150 и никогда не печатался. Мёртвые view-поля, byte-parity не задет.
      bg1: bgArr(b.BG1),
      bg2: bgJoin(b.BG2),
      bg3: bgJoin(b.BG3),
      bg4: bgJoin(b.BG4),
    };
  });
  // Копия правила снята (cc-32): концы дуги приходят из `lifeArc`, того же места, что питает §I и
  // §III. §II несёт то же УТВЕРЖДЕНИЕ о размахе жизни, что обложка, и молчит там же, где она.
  const arcII = lifeArc(passport, readingYear);
  const years = arcII.years === null ? null : `${arcII.firstY} → ${arcII.lastY}`;

  return {
    ...(screen != null ? { screen } : {}), ...(source != null ? { source } : {}),
    stages, means, carriers,
    ...(years != null ? { years } : {}),
    ...(plan ? { plan } : {}),
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// §I · COVER
// DETERMINISTIC: stageCount / geometryClass / silhouette / stageMeta / curIdx / arc*.
// No board, no browser measurement — the cover has no fitted field.
//
// WHY THIS IS A DERIVER NOW (cc-31). It used to `return passport`, and SectionI dug into
// `passport.input.wizard.stages` itself — the ONE section still reading wizard state outside the
// seam. Two things came from that: the derivation was invisible to anyone reading the seam, and
// §I's own degradation rule drifted away from its neighbours' (see `lastEnd` below). Guard —
// `sections/seam.test.js`: direct passport/wizard reads inside a section renderer must be zero.
// ─────────────────────────────────────────────────────────────────────────────
export function viewI(passport, opts = {}) {
  const { screen, source, readingYear = READING_YEAR, generatedAt } = opts;
  const stageCount = passport.stageCount;

  // Stage meta (names/dates) is usable ONLY when it lines up 1:1 with the silhouette stages. An
  // empty array, a shorter/longer list, or holes desync the two parallel arrays and would crash
  // the arc labels (stageMeta[0] / stageMeta[N-1]) and the chart's current-node index — so any
  // mismatch falls back to the dateless canon form (same path as no stages at all). A real wizard
  // session always matches; a mismatch is upstream corruption we degrade past rather than crash.
  const rawMeta = passport.input?.wizard?.stages;
  const stageMeta =
    Array.isArray(rawMeta) && rawMeta.length === stageCount && rawMeta.every((s) => s && typeof s === 'object')
      ? rawMeta
      : null;

  // current stage from the wizard flags; -1 ⇒ none (chart drops halo/ember/NOW).
  // findLAST, не findFirst (QA cc-31): при двух поднятых флагах `current` §I ставил NOW на
  // САМЫЙ СТАРЫЙ этап и рисовал ember у левого края, пока §II и §VII читали «сейчас» как
  // последний этап. Флаг на нескольких этапах валидатор пропускает (предупреждение
  // совещательное), так что расходились они на легальном вводе.
  const curIdx = stageMeta ? stageMeta.findLastIndex((s) => s.current) : stageCount - 1;
  const hasCurrent = curIdx >= 0;
  // «→ NOW» на дуге законно, только если текущий этап И ЕСТЬ последний. Иначе обложка на одном
  // листе печатала бровь «'05 → NOW» и футер «2005 → 2020» — сама себе противореча (QA cc-31).
  const nowEndsArc = curIdx === stageCount - 1;

  // arc: first recorded year → last (NOW when the last stage is current). arcShort (short left
  // label) only when the first stage is open-started (CD variant 06).
  const firstS = stageMeta ? stageMeta[0] : null;
  // Открытое начало → крайнего года НЕТ, и дуга не печатается ни одной из двух форм: канон F7
  // v06 / E4 держит `arcYears: null`, `arcShort: true`, `arcShortLabel: ''`. До cc-31 здесь
  // стоял откат на `firstS.endY`, а под моделью А это начало ВТОРОГО этапа — обложка печатала
  // «2009 → 2026» там, где канон печатает «5 stages» без годов. Поймано QA-прогоном.
  const openStart = !!(firstS && firstS.openStart);
  // КОНЦЫ ДУГИ СЧИТАЕТ `lifeArc` — одно место на §I, §II и §III (cc-32). Само правило и его
  // защиты описаны там; здесь оставлена история, ради которой каждая защита появилась.
  //
  // An OPEN last stage with a blank end anchors to the reading year — the same rule §VII uses for
  // `nowYear` and §II for its `years` row. Before cc-31 §I alone read `endY` raw, so a passport
  // built without `nowY` (a legal degradation: `toWizardInput` writes `nowY ?? null`) silently
  // lost BOTH arc forms at once, with no validator or submit-check complaining. Ledger, line 27.
  // Третья ветка — НЕТ конца и НЕТ флага `current`: в данных о конце не сказано ничего. Раньше
  // здесь стояло начало последнего этапа, то есть обложка утверждала, что последняя глава жизни
  // длилась ноль. Теперь дуга не печатается вовсе — та же деградация, что канон F7 держит для
  // открытого начала (v06): «N stages» без годов. Единственный вариант, который не выдумывает.
  // Сегодня ветка недостижима: и визард (`toWizardInput`), и парсер биографий
  // (`parse-extract.mjs:63`) ставят флаг последнему этапу принудительно. Оборона на завтра.
  // ЯКОРЬ НЕ ВСЕСИЛЕН. Год чтения — заморожённое число, а этапы человек проставляет сам, и они
  // могут лежать позже него: сабмит без `nowY` в 2027, или просто опечатка в году. Тогда якорь
  // закрывал дугу РАНЬШЕ, чем открывалась последняя глава: «2000 → 2026» на листе, где строкой
  // ниже стоит «01/2027 — present», а на крайнем входе — прямая инверсия «2027 → 2026». Валидатор
  // чист, предупреждений нет. Правило: якорь годится, только если он не раньше начала того самого
  // этапа, который им закрывают; иначе честного конца нет — и дуга не печатается (QA cc-31).
  // Number.isFinite, не typeof: NaN проходит проверку типа и печатается на бумаге — «2000 → NaN»
  // в футере и тик `'aN` на оси (QA cc-31). Живой путь NaN не даёт, но §I вызывают и напрямую.
  const arcI = lifeArc(passport, readingYear);
  const { first, last } = arcI;
  // ГАРД ПЕЧАТИ — `years`, а не `dated` (QA cc-33). `lifeArc` уже снимает размах, когда конец
  // раньше начала (`lastY >= firstY`), и §II с §III на этот гард опираются. §I мимо него ходила:
  // обе формы дуги строились из голого `dated`, то есть «даты есть» вместо «размах печатаем».
  // Замер 21.08 на входе `a 2005→2010, b 2000→2003` (validateWizardInput чист, только совещательное
  // «stages must run oldest-first»): обложка печатала футер **«2005 → 2003»** и бровь **«'05 → '03»**,
  // а §II в тот же момент не печатала строку годов вовсе, §III — ни одного «N years on one map».
  // Ровно тот класс, ради которого правило и сводили в одно место: три секции, три ответа, один лист.
  // Дуга, идущая назад, — не деградация, а утверждение наоборот; при перевёрнутом размахе §I молчит
  // вместе с соседями. Канон не двигается: у всех датасетов и бордов даты хронологичны.
  const dated = arcI.years !== null;

  return {
    ...(screen != null ? { screen } : {}), ...(source != null ? { source } : {}),
    stageCount,
    geometryClass: passport.system1?.redLine?.geometryClass,
    silhouette: passport.silhouette,
    // NOT the wizard's stage objects. Handing `stageMeta` through would have let a renderer
    // read `endY` — and re-implement the degradation rule — without ever naming `passport`,
    // which is exactly the leak the seam guard cannot see (QA, cc-31: the arc vanished again
    // through a view that looked clean). The chart needs the labels, so the seam derives the
    // labels; the raw stages stop at this line.
    tickLabels: deriveTickLabels(stageMeta, stageCount, curIdx),
    curIdx,
    hasCurrent,
    dated,
    arcShort: openStart, // защита была на двух чтениях из трёх — теперь одна на всех
    arcYears: dated ? `${Math.floor(first)} → ${Math.floor(last)}` : null,
    // канон при открытом начале держит пустую строку, не null — форму сохраняем дословно
    arcShortLabel: openStart ? '' : (dated ? `${yy(first)} → ${nowEndsArc ? 'NOW' : yy(last)}` : null),
    // дата генерации (docDate.js) — штамп печатной дороги; нет его → слот остаётся заглушкой
    ...(generatedAt ? { generatedAt } : {}),
  };
}

const DERIVERS = {
  I: viewI,
  II: viewII,
  III: viewIII,
  IV: viewIV,
  V: viewV,
  VI: viewVI,
  VII: viewVII,
};

/**
 * Derive a section's view from a live passport.
 * @param {string} sectionId  one of 'I'..'VII'
 * @param {object} passport   buildPassport() output
 * @param {object} [opts]     screen/source labels + cross-section flags (skipVi, absent, …)
 */
export function viewFromPassport(sectionId, passport, opts = {}) {
  const fn = DERIVERS[sectionId];
  if (!fn) throw new Error(`viewFromPassport: no deriver for §${sectionId} yet`);
  return fn(passport, opts);
}
